import BigNumber from 'bignumber.js'
import superscript from 'superscript-number'
import type { Currency } from '@/types/services/global'

/**
 * Abbreviate big numbers i.e. 123456789.89 = 123,45 Mil
 * @param {(number|string)} value - Amount
 * @param {boolean} shortSymbol - Use shor symbols like K, M, etc. or use k, mil, bil, etc.
 * @param {number} digits - Digits after decimal point
 */
const abbreviateNumber = (
  value: number | string,
  shortSymbol = false,
  digits = 2
): string => {
  const bn = new BigNumber(value)

  // Return empty string if NaN
  if (bn.isNaN()) {
    return ''
  }

  if (!bn.isFinite()) {
    return bn.toString()
  }

  const symbols = shortSymbol
    ? ['', 'K', 'M', 'B', 'T', 'Q']
    : ['', 'k', ' mil', ' bil', ' tril', ' qa']

  const si = [
    { value: 1, symbol: symbols[0] },
    { value: 1e3, symbol: symbols[1] },
    { value: 1e6, symbol: symbols[2] },
    { value: 1e9, symbol: symbols[3] },
    { value: 1e12, symbol: symbols[4] },
    { value: 1e15, symbol: symbols[5] },
  ]

  const rx = /\.0+$|(?<nonzerosdecimals>\.[0-9]*[1-9])0+$/u
  let i: number | null = null
  for (i = si.length - 1; i > 0; i--) {
    if (bn.abs().isGreaterThanOrEqualTo(si[i].value)) {
      break
    }
  }
  return (
    bn
      .dividedBy(si[i].value)
      .toFixed(digits)
      .replace(rx, '$<nonzerosdecimals>') + si[i].symbol
  )
}

/**
 * Format a numeric value to a more user-friendly
 * format (i.e. 1000234 -> 1,000,234)
 * @param {number} value - Negative or positive number
 * @param {object} settings - Formatting settings
 * @returns {string} - Return formatted value
 */
const smartFormat = (value: number | string, settings: any = {}): string => {
  // Merge settings
  const _settings = {
    decimalPlaces: 2,
    trimZeros: true,
    rounding: true,
    maxAbsoluteValue: 1e12,
    disableAbbr: false,
    ...settings,
  }
  const { decimalPlaces, trimZeros, rounding } = _settings

  let bn = new BigNumber(value)

  // Return empty string if NaN
  if (bn.isNaN()) {
    return ''
  }

  // A negative zero keeps its sign, which `toFormat` renders as `-.00.00`
  if (bn.isZero()) {
    bn = new BigNumber(0)
  }

  if (!rounding) {
    return bn.toFormat()
  }

  // If the number is too small or too large,
  // show it in exponential notation
  if (bn.absoluteValue().isLessThan(0.1)) {
    let shift = decimalPlaces
    if (!bn.isZero()) {
      while (bn.absoluteValue().shiftedBy(shift).isLessThan(1)) {
        shift++
      }
      shift += 1
    }

    return trimZeros ? bn.decimalPlaces(shift).toFormat() : bn.toFormat(shift)
  } else if (
    bn.absoluteValue().isGreaterThanOrEqualTo(_settings.maxAbsoluteValue) &&
    !_settings.disableAbbr
  ) {
    return abbreviateNumber(bn.toNumber())
  }

  return trimZeros
    ? bn.decimalPlaces(decimalPlaces).toFormat()
    : bn.toFormat(decimalPlaces)
}

/**
 * abbreviateNumber with an explicit '+' for positive values
 */
const signedAbbreviateNumber = (
  value: number | string,
  shortSymbol = false,
  digits = 2
): string => {
  const formatted = abbreviateNumber(value, shortSymbol, digits)
  return new BigNumber(value || 0).isGreaterThan(0)
    ? `+${formatted}`
    : formatted
}

/**
 * Keeps unified format for percentage numbers
 * @param {(number|string)} value - Percentage value (i.e. 77.5)
 * @returns {string} - Formatted percentage string
 */
const percentageFormat = (value: number | string, decimals = 2): string => {
  // Avoid NaN by casting to 0 if number is undefined
  const bn = new BigNumber(value || 0)

  const formatted = bn.toFormat(decimals)
  const negativeZero = `-0.${'0'.repeat(decimals)}`
  if (formatted === negativeZero) {
    return `0.${'0'.repeat(decimals)}`
  }
  return formatted
}

/**
 * percentageFormat with an explicit '+' for positive values
 */
const signedPercentageFormat = (
  value: number | string,
  decimals = 2
): string => {
  const formatted = percentageFormat(value, decimals)
  return new BigNumber(value || 0).isGreaterThan(0)
    ? `+${formatted}`
    : formatted
}

/**
 * Converts a decimal percentage to a whole number percentage
 * and fix precision problem, e.g.: 0.57*100 = 56.99999999999999
 *
 * @param {number} value - The decimal percentage to convert
 * @param {number} [digits=-1] - The number of decimal places to round to
 * @returns {number} - The whole number percentage
 */
const decimalToPercent = (value: number, digits = -1): number => {
  const bn = new BigNumber(value).shiftedBy(2)

  if (digits >= 0) {
    return bn.decimalPlaces(digits).toNumber()
  }

  return bn.toNumber()
}

/**
 * Converts a percentage value to a decimal representation of the percentage.
 *
 * @param {number} value - The percentage value to convert (e.g. 50 for 50%)
 * @returns {number} - The decimal equivalent of the input percentage value
 */
const percentToDecimal = (value: number): number => {
  return new BigNumber(value).shiftedBy(-2).toNumber()
}

/**
 * Keeps unified format for pagination page numbers
 * (i.e. 6 -> 06)
 * @param {number} page - Page number
 * @returns {string} - Formatted page number
 */
const pageFormat = (page: number): string => {
  // Turn "1" into "01"
  return ('0' + page).slice(-2)
}

const roundNumber = (value: number, decimalPlaces = 2): number => {
  return new BigNumber(value).decimalPlaces(decimalPlaces).toNumber()
}

/**
 * Formats scientific notation written with "e".
 * Replaces "e" with "x 10" and wraps it in html
 * tags for styling (i.e. "1.23e-11" -> "1.23 x 10<sup>11</sup>")
 * @param {string} value - Number
 * @param {boolean} isHtml - is return value formatted with html
 * @returns {string} - Formatted string
 */
const notationFormat = (value: string, isHtml = true): string => {
  const [val, sup] = value.split('e')
  if (!sup) {
    return value
  }

  // Remove "+" sign from superspcript
  const _sup = sup.replace('+', '')

  if (isHtml) {
    return `${val} × 10<sup>${_sup}</sup>`
  } else {
    return `${val} × 10${superscript(parseInt(sup, 10))}`
  }
}

/**
 *
 * @param {(string|number)} value - number
 * @param {boolean} isHtml - is return value formatted with html
 * @param {number} minDecimalPlaces - minimum of decimal places
 * @returns {string} - returns rounded-md number to last 2 (or another specified) non-zero decimal places.
 * String can be HTML formatted with adjusted font size for small numbers
 */
const shrinkDecimals = (
  value: string | number | null | undefined,
  isHtml = true,
  minDecimalPlaces = 2
): string => {
  if (
    value === '' ||
    (typeof value !== 'number' && typeof value !== 'string')
  ) {
    return '-'
  }

  if (typeof value === 'number') {
    value = value.toString()
  }
  if (value === '0') {
    return minDecimalPlaces
      ? value + '.'.padEnd(minDecimalPlaces + 1, '0')
      : value
  }
  value = value.replace(/,/g, '')

  // convert number strings like 1.7740467780051016E-8 to 0.000000017740467780051016
  const idxOfMinusE = value.toLowerCase().indexOf('e-')
  if (idxOfMinusE !== -1) {
    const zeros = Number(value.substring(idxOfMinusE + 2))
    if (zeros) {
      let zerosStr = ''
      for (let i = 1; i < zeros; i++) {
        zerosStr += '0'
      }
      value = `0.${zerosStr}${value.slice(0, idxOfMinusE).replace('.', '')}`
    }
  }
  // Split number
  let afterDecimal = value.split('.')[1]
  if (!afterDecimal || Number(afterDecimal) === 0) {
    afterDecimal = ''
  }

  // This adds two decimal places after a span of zeros
  let decimalPlaces = 2

  // Count zeros before first number to know
  // how many decimal places to use for rounding
  for (let i = 0; i < afterDecimal.length; i++) {
    if (afterDecimal[i] !== '0') {
      break
    }
    decimalPlaces++
  }
  if (decimalPlaces < minDecimalPlaces) {
    decimalPlaces = minDecimalPlaces
  }

  const rounded = new BigNumber(value).toFormat(decimalPlaces)

  // eslint-disable-next-line prefer-const
  let [roundedPreDecimal, roundedAfterDecimal] = rounded.split('.')

  // remove trailing spaces
  for (let i = roundedAfterDecimal.length - 1; i >= minDecimalPlaces; i--) {
    if (roundedAfterDecimal[i] !== '0') {
      break
    }
    roundedAfterDecimal = roundedAfterDecimal.substring(
      0,
      roundedAfterDecimal.length - 1
    )
  }

  if (isHtml && (roundedPreDecimal === '0' || roundedPreDecimal === '-0')) {
    return `${roundedPreDecimal}.<span class="text-xss">${roundedAfterDecimal}</span>`
  }
  return roundedAfterDecimal
    ? `${roundedPreDecimal}.${roundedAfterDecimal}`
    : roundedPreDecimal
}

/**
 * Return shorten Tx Hash (first 6 characters, triple-dot, last 4 characters)
 * @param {string} hash - Tx Hash
 */
const shortenTxHash = (hash: string): string => {
  const start = hash.substring(0, 6)
  const end = hash.slice(-4)
  return start + '...' + end
}

/**
 * Format number value with default smart format function and add currency symbol
 * @param value
 * @param currency
 */
const formatWithCurrency = (value: number, currency: Currency | null) => {
  return `${smartFormat(value, { trimZeros: false })} ${currency?.symbol || '-'}`
}

/**
 * formatWithCurrency with an explicit '+' for positive values
 */
const signedFormatWithCurrency = (value: number, currency: Currency | null) => {
  const sign = value > 0 ? '+' : ''
  return `${sign}${formatWithCurrency(value, currency)}`
}

export {
  smartFormat,
  percentageFormat,
  signedPercentageFormat,
  decimalToPercent,
  percentToDecimal,
  roundNumber,
  pageFormat,
  notationFormat,
  shrinkDecimals,
  shortenTxHash,
  abbreviateNumber,
  signedAbbreviateNumber,
  formatWithCurrency,
  signedFormatWithCurrency,
}
